import React, { useCallback, useMemo } from 'react'
import { Button, VerticalExpander, Text, InfoPill } from '../../../elementals'
import * as constants from '../../../../constants'
import * as hooks from '../../../../hooks'
import * as graph from '@adc/sdk'
import classnames from 'classnames/bind'
// @ts-ignore no types
import _ from 'lodash'
// @ts-ignore no types
import styles from './styles.scss'
import {
  CurrentTargetAudience,
  Maybe,
  OnlineAuctionSegmentBid,
  SealedBidInput,
} from '@adc/sdk/types'
import { ButtonProps } from 'semantic-ui-react'
import SealedBidForm from '../bidForm'
import { InferBoundAuctionBlueprintCollectionElement } from '@adc/blueprint/lib/primitives/types'
import {
  ListingsData,
  OnlineAuctionSegmentsData,
} from '@adc/blueprint/lib/bidding/types'
import { useSealedBidForm } from '../../../../utils/sealedBid'
import GeneralModal, { GeneralModalProps } from '../../../overlays/generalModal'
import type { ActionModalButtonProps } from '../../../overlays/modal/actionModalButton'

const cx = classnames.bind(styles)
const { ColorNames } = constants

const UPDATE_OFFER_TEXT = 'Update Offer'

interface UpdateOfferModalButtonProps extends GeneralModalProps {
  submitCallback: (input: { sealedBidInput: SealedBidInput }) => Promise<void>
  cancelCallback?: () => void
  modalTitle: React.ReactNode
  confirmButtonText?: string
  activeModal?: string
  setActiveModal?: (modal: string) => void
  listing: InferBoundAuctionBlueprintCollectionElement<ListingsData['mapped']>
  segment?: Maybe<
    InferBoundAuctionBlueprintCollectionElement<
      OnlineAuctionSegmentsData['mapped']
    >
  >
  modalId?: string
  headers?: string[]
  bid: OnlineAuctionSegmentBid
  readOnly?: boolean
  disabled?: boolean
  viewingFrom: CurrentTargetAudience
  classNames?: {
    button?: string
    modal?: string
    form?: string
  }
  buttonProps?: ButtonProps
  formRef?: React.RefObject<HTMLFormElement>
}

const UpdateOfferModalButton = ({
  submitCallback,
  listing,
  segment,
  confirmButtonText = 'Send Updated Offer',
  modalTitle,
  headers,
  bid,
  readOnly = false,
  cancelCallback = () => false,
  disabled = false,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  classNames = {},
  buttonProps = {},
  actions,
  preventClose = true,
  formRef,
  ...rest
}: UpdateOfferModalButtonProps) => {
  const [showModal, setShowModal] = React.useState(false)
  const [isUpdating, setUpdating] = React.useState(false)

  const isBuyerView = graph.enums.CurrentTargetAudience.Buyer === viewingFrom
  const isDisabled = useMemo(
    () =>
      disabled ||
      isUpdating ||
      (isBuyerView &&
        segment &&
        [
          graph.enums.OnlineAuctionSegmentStatus.HighestAndBestDecision,
        ].includes(segment?.segment_status)),
    [disabled, segment, isBuyerView, isUpdating]
  )

  const { data, errors, dirty, config, components, reset, validate } =
    useSealedBidForm({
      bid,
      headers,
      listing,
      segment,
      readOnly,
      canUpdateMyOffer: true,
      viewingFrom,
    })

  const breakpoint = hooks.useBreakpoints()
  const isMobile = breakpoint === 'mobile'

  const handleCloseModal = useCallback(() => {
    setShowModal(false)
    reset()
  }, [reset])

  const handleCancel = useCallback(() => {
    handleCloseModal()
    cancelCallback()
  }, [handleCloseModal, cancelCallback])

  const handleSubmit = useCallback(async () => {
    if (!data || !validate()) return
    setUpdating(true)
    await submitCallback({
      sealedBidInput: data,
    })
    setUpdating(false)
    handleCloseModal()
  }, [data, validate, submitCallback, handleCloseModal])

  const header = useMemo(() => {
    if (modalTitle) return modalTitle
    return (
      <>
        <div className={cx('modal-title')}>
          <Text size='big' color='black'>
            Update Offer
          </Text>
          {!isMobile && (
            <>
              <Text size='small' color='gray'>
                Use the fields below to update your offer
              </Text>
              <div className='u-mt-2 u-mb-1'>
                <InfoPill
                  size='medium'
                  includeBorder
                  backgroundColor={ColorNames.InfoExtraLight}
                  foregroundColor={ColorNames.Info}
                  iconName='medal'
                  label={
                    // TODO: consolidate this with the infopill from buildingBlocks in PDP
                    <Text size='small' color={ColorNames.Info}>
                      Update your offer to see where you stand on the{' '}
                      <strong>Leaderboard!</strong>
                    </Text>
                  }
                />
              </div>
            </>
          )}
        </div>
      </>
    )
  }, [modalTitle])

  const handleShowModal = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowModal(true)
  }, [])

  const actionButtons = useMemo(() => {
    if (actions) return actions
    return [
      {
        label: 'Cancel',
        elmId: 'update_offer_cancel_button',
        onClick: handleCancel,
      },
      {
        label: confirmButtonText,
        style: { marginLeft: '12px' },
        onClick: handleSubmit,
        disabled: isDisabled || !dirty || !_.isEmpty(errors),
        loading: isUpdating,
        elmId: 'update_offer_confirm_button',
        isCountdownButton: true,
        reInitialize: true,
        role: 'primary',
      },
    ] as Array<ActionModalButtonProps>
  }, [
    actions,
    confirmButtonText,
    isDisabled,
    isUpdating,
    dirty,
    errors,
    handleCancel,
    handleSubmit,
  ])

  return (
    <>
      <Button
        size='large'
        role='secondary'
        {..._.omit(buttonProps, 'label')}
        onClick={handleShowModal}
        className={cx('large-fixed-button', classNames?.button)}
        elmId={`update_offer_button${isDisabled ? '_disabled' : ''}`}
        disabled={isDisabled}
      >
        {(buttonProps?.label as React.ReactNode) ?? UPDATE_OFFER_TEXT}
      </Button>
      <GeneralModal
        {...rest}
        open={showModal}
        title={header}
        className={cx('update-offer-modal', classNames?.modal)}
        elmId='update_offer_elm_id'
        mobileTitleSkin='light'
        onClose={handleCancel}
        size='large'
        actions={actionButtons}
        preventClose={preventClose}
      >
        <VerticalExpander>
          <SealedBidForm
            className={classNames?.form}
            config={config}
            slots={{
              components,
            }}
            bid={bid}
            listing={listing}
            readOnly={readOnly}
            viewingFrom={viewingFrom}
            onSubmit={handleSubmit}
            formRef={formRef}
          />
        </VerticalExpander>
      </GeneralModal>
    </>
  )
}

export default UpdateOfferModalButton
